import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { VoiceTestClient } from '@/utils/VoiceTestClient';
import { Phone, PhoneOff, Mic, Volume2, Radio, Zap, Activity, Wrench } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { CalBookingWidget } from '@/components/CalIntegration/CalBookingWidget';
import { isSearchWithoutSources, NO_SOURCES_TEXT } from '@/utils/google-search-card';

type ConversationItem = 
  | { type: 'transcript'; id: string; role: 'user' | 'agent'; text: string; timestamp: Date }
  | { type: 'tool_call'; id: string; toolName: string; args: any; timestamp: Date }
  | { type: 'tool_response'; id: string; toolName: string; response: any; timestamp: Date }
  | { type: 'tool_error'; id: string; toolName: string; error: string; timestamp: Date };

const VoiceTest = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [isConnected, setIsConnected] = useState(false);
  const [conversation, setConversation] = useState<ConversationItem[]>([]);
  const [expandedToolCalls, setExpandedToolCalls] = useState<Set<string>>(new Set());
  const [permissionStatus, setPermissionStatus] = useState<'pending' | 'granted' | 'denied'>('pending');
  const [callEndedReason, setCallEndedReason] = useState<string | null>(null);
  const [callEndedMessage, setCallEndedMessage] = useState<string | null>(null);
  const clientRef = useRef<VoiceTestClient | null>(null);


  useEffect(() => {
    if (!agentId) {
      toast({
        title: 'No Agent Selected',
        description: 'Please select an agent to test',
        variant: 'destructive'
      });
      navigate('/agents');
    }
  }, [agentId, navigate, toast]);

  const handleConnect = async () => {
    if (!agentId) return;

    // Show permission request toast
    toast({
      title: 'Microphone Access Required',
      description: 'Please allow microphone access to continue'
    });
    
    setPermissionStatus('pending');
    let micGranted = false;

    try {
      // Request permission before creating client
      await navigator.mediaDevices.getUserMedia({ audio: true });
      micGranted = true;
      setPermissionStatus('granted');
      
      toast({
        title: 'Permission Granted',
        description: 'Connecting to voice server...'
      });

      const client = new VoiceTestClient(
      (role, text) => {
        setConversation(prev => [...prev, {
          type: 'transcript',
          id: `transcript-${Date.now()}-${Math.random()}`,
          role,
          text,
          timestamp: new Date()
        }]);
      },
      (toolName, args) => {
        setConversation(prev => [...prev, {
          type: 'tool_call',
          id: `tool-call-${Date.now()}-${Math.random()}`,
          toolName,
          args,
          timestamp: new Date()
        }]);
      },
      (toolName, response) => {
        setConversation(prev => [...prev, {
          type: 'tool_response',
          id: `tool-response-${Date.now()}-${Math.random()}`,
          toolName,
          response,
          timestamp: new Date()
        }]);
      },
      (toolName, error) => {
        setConversation(prev => [...prev, {
          type: 'tool_error',
          id: `tool-error-${Date.now()}-${Math.random()}`,
          toolName,
          error,
          timestamp: new Date()
        }]);
      },
      (error) => {
        toast({
          title: 'Error',
          description: error,
          variant: 'destructive'
        });
      },
      (connected) => {
        setIsConnected(connected);
        if (connected) {
          toast({
            title: 'Connected',
            description: 'Voice test session started'
          });
          // Clear previous call ended state when reconnecting
          setCallEndedReason(null);
          setCallEndedMessage(null);
        }
      },
      (reason, message) => {
        // Call ended from server side
        setCallEndedReason(reason);
        setCallEndedMessage(message);
        setIsConnected(false);
        toast({
          title: 'Call Ended',
          description: message || 'The call has ended',
          variant: 'default'
        });
      }
      );

      clientRef.current = client;
      
      // Connect via Pipecat Cloud WebRTC
      await client.connect(agentId);
      
    } catch (error) {
      // Only the microphone step is about the microphone; after it, say what
      // really failed (a deleted agent: "This agent was deleted.").
      if (micGranted) {
        toast({
          title: 'Connection Failed',
          description: error instanceof Error ? error.message : 'Failed to connect to voice server',
          variant: 'destructive'
        });
        return;
      }
      setPermissionStatus('denied');
      toast({
        title: 'Microphone Access Denied',
        description: 'Please allow microphone access in your browser settings',
        variant: 'destructive'
      });
    }
  };

  const handleDisconnect = () => {
    clientRef.current?.disconnect();
    clientRef.current = null;
    // Note: Conversation logs are preserved - they will only be cleared on page refresh or component unmount
  };

  useEffect(() => {
    return () => {
      clientRef.current?.disconnect();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header with gradient */}
        <div className="flex items-center justify-between pb-4 border-b border-border/50">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-primary to-primary/80 shadow-lg">
                <Radio className="w-6 h-6 text-primary-foreground" />
              </div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                Voice Agent Test
              </h1>
            </div>
            <p className="text-muted-foreground ml-14">Real-time voice AI testing interface</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/agents')} className="shadow-sm">
            Back to Agents
          </Button>
        </div>

        {/* Connection Controls - Modern Card */}
        <Card className="border-2 shadow-xl bg-card/50 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Activity className="w-5 h-5 text-primary" />
                  Session Control
                </CardTitle>
                <CardDescription className="mt-1">Manage your voice test session</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4 flex-wrap">
              {!isConnected ? (
                <Button 
                  onClick={handleConnect} 
                  size="lg" 
                  className="gap-2 shadow-lg hover:shadow-xl transition-all duration-200 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-primary-foreground font-semibold"
                >
                  <Phone className="w-5 h-5" />
                  {callEndedReason ? 'Reconnect' : 'Start Voice Test'}
                </Button>
              ) : (
                <Button 
                  onClick={handleDisconnect} 
                  size="lg" 
                  variant="destructive" 
                  className="gap-2 shadow-lg hover:shadow-xl transition-all duration-200 font-semibold"
                >
                  <PhoneOff className="w-5 h-5" />
                  End Session
                </Button>
              )}
              
              {callEndedReason && (
                <div className="px-4 py-2 rounded-lg bg-orange-50 border border-orange-200 dark:bg-orange-950/20 dark:border-orange-800">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100">
                      Call Ended
                    </Badge>
                    <span className="text-sm text-orange-700 dark:text-orange-400">
                      {callEndedMessage || 'The call has ended'}
                    </span>
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-muted/50 border border-border/50">
                <div className={`relative w-3 h-3 ${isConnected ? 'bg-green-500' : 'bg-muted-foreground/30'}`}>
                  {isConnected && (
                    <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-75" />
                  )}
                  <div className={`absolute inset-0 rounded-full ${isConnected ? 'bg-green-500' : 'bg-muted-foreground/30'}`} />
                </div>
                <span className="text-sm font-medium">
                  {isConnected ? 'Connected' : 'Disconnected'}
                </span>
              </div>

              {isConnected && (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="gap-1.5 px-3 py-1.5 bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20">
                    <Mic className="w-3.5 h-3.5" />
                    <span className="font-medium">Listening</span>
                  </Badge>
                  <Badge variant="secondary" className="gap-1.5 px-3 py-1.5 bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/20">
                    <Volume2 className="w-3.5 h-3.5" />
                    <span className="font-medium">Playing</span>
                  </Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Conversation - Modern Design */}
        <Card className="border-2 shadow-xl bg-card/50 backdrop-blur-sm">
          <CardHeader className="border-b border-border/50 bg-gradient-to-r from-muted/30 to-transparent">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Zap className="w-5 h-5 text-primary" />
                  Live Conversation
                </CardTitle>
                <CardDescription className="mt-1">Real-time tool execution logs</CardDescription>
              </div>
              {(() => {
                const toolCount = conversation.filter((item) => item.type !== 'transcript').length;
                return toolCount > 0 && (
                  <Badge variant="outline" className="px-3 py-1">
                    {toolCount} {toolCount === 1 ? 'tool execution' : 'tool executions'}
                  </Badge>
                );
              })()}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[600px]">
              <div className="p-6">
                {(() => {
                  const toolItems = conversation.filter((item) => item.type !== 'transcript');
                  return toolItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <div className="p-4 rounded-full bg-muted/50 mb-4">
                        <Wrench className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <p className="text-muted-foreground font-medium">No tool executions yet</p>
                      <p className="text-sm text-muted-foreground/70 mt-1">Tool execution logs will appear here when the agent uses tools</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {toolItems.map((item) => {
                      if (item.type === 'tool_call') {
                      return (
                        <div key={item.id} className="p-3 rounded-lg bg-accent/50 border border-primary/20">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="bg-primary/10">🔧 {item.toolName}</Badge>
                            <span className="text-xs text-muted-foreground">
                              {item.timestamp.toLocaleTimeString()}
                            </span>
                          </div>
                          <div>
                            <div className="text-xs font-semibold mb-1">Arguments:</div>
                            <pre className="text-xs bg-background p-2 rounded overflow-x-auto whitespace-pre-wrap break-all max-w-full">
                              {JSON.stringify(item.args, null, 2)}
                            </pre>
                          </div>
                        </div>
                      );
                    }
                    
                    if (item.type === 'tool_response') {
                      // Show Cal.com booking widget for availability checks
                      if (item.toolName === 'check_availability_and_requirements' && item.response) {
                        return (
                          <div key={item.id} className="w-full">
                            <CalBookingWidget
                              toolResponse={item.response}
                              onSlotSelect={(slot) => {
                                console.log('Selected slot:', slot);
                                // You can add logic here to trigger booking or update state
                              }}
                            />
                          </div>
                        );
                      }
                      
                      // Default tool response display
                      return (
                        <div key={item.id} className="p-3 rounded-lg bg-green-50 border border-green-200 dark:bg-green-950/20 dark:border-green-800">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
                              ✓ {item.toolName} Response
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {item.timestamp.toLocaleTimeString()}
                            </span>
                          </div>
                          <div>
                            <div className="text-xs font-semibold mb-1 text-green-700 dark:text-green-400">Response:</div>
                            <pre className="text-xs bg-background p-2 rounded overflow-x-auto whitespace-pre-wrap break-all max-w-full">
                              {isSearchWithoutSources(item.toolName, item.response) ? NO_SOURCES_TEXT : JSON.stringify(item.response, null, 2)}
                            </pre>
                          </div>
                        </div>
                      );
                    }
                    
                    if (item.type === 'tool_error') {
                      return (
                        <div key={item.id} className="p-3 rounded-lg bg-red-50 border border-red-200 dark:bg-red-950/20 dark:border-red-800">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="destructive">
                              ✗ {item.toolName} Error
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {item.timestamp.toLocaleTimeString()}
                            </span>
                          </div>
                          <div>
                            <div className="text-xs font-semibold mb-1 text-red-700 dark:text-red-400">Error:</div>
                            <div className="text-xs bg-background p-2 rounded break-words">
                              {item.error}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    return null;
                  })}
                </div>
              );
            })()}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Quick Guide - Compact */}
        <Card className="border-2 shadow-lg bg-gradient-to-br from-muted/30 to-transparent">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="w-4 h-4 text-primary" />
              Quick Guide
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="flex items-start gap-2 p-2 rounded-lg bg-background/50 border border-border/50">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary">1</span>
                </div>
                <p className="text-xs text-muted-foreground">Start session & allow mic access</p>
              </div>
              <div className="flex items-start gap-2 p-2 rounded-lg bg-background/50 border border-border/50">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary">2</span>
                </div>
                <p className="text-xs text-muted-foreground">Speak naturally to test the agent</p>
              </div>
              <div className="flex items-start gap-2 p-2 rounded-lg bg-background/50 border border-border/50">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary">3</span>
                </div>
                <p className="text-xs text-muted-foreground">Monitor real-time tool execution</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VoiceTest;
